import { createTheme } from "@mui/material";
import { accentTheme } from "../theme/accent";

export const getReadTime = (blog = {}) => {
  const content = [blog.description, blog.content].filter(Boolean).join(" ");
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 10))} min read`;
};
export const scrollToTop = (behavior = "smooth") => {
  if (typeof window !== "undefined") {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior,
    });
  }
};
export const getInitials = (name = "A") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";

export const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toDateString();
};

export const darkTheme = createTheme({
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
  },
  palette: {
    mode: "dark",
    primary: {
      main: accentTheme.primaryDarkMode,
    },
    text: {
      primary: "#f6efe6",
      secondary: "rgba(241,237,230,0.68)",
      body: accentTheme.primaryDarkMode,
      watermark: "#ffffff12",
    },
    background: {
      default: "#06080f",
      paper: "rgba(18,23,34,0.78)",
      dark: "rgba(18,23,34,0.78)",
    },
    action: {
      hover: accentTheme.primaryDarkMode,
    },
    accent: {
      main: accentTheme.primaryDarkMode,
      light: accentTheme.darkModeSoft,
      dark: accentTheme.deep,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          colorScheme: "dark",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: "rgba(18,23,34,0.78)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow:
            "0 24px 54px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.08)",
          backdropFilter: "blur(24px) saturate(140%)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 14,
        },
        contained: {
          background: `linear-gradient(135deg, ${accentTheme.primaryDarkMode} 0%, ${accentTheme.deep} 100%)`,
          boxShadow: `0 10px 26px ${accentTheme.glow}`,
        },
        outlined: {
          borderColor: "rgba(255,255,255,0.14)",
          background: "rgba(255,255,255,0.03)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: "rgba(255,255,255,0.04)",
          borderRadius: 14,
          "& fieldset": {
            borderColor: "rgba(255,255,255,0.12)",
          },
          "&:hover fieldset": {
            borderColor: accentTheme.border,
          },
          "&.Mui-focused fieldset": {
            borderColor: accentTheme.primaryDarkMode,
            boxShadow: `0 0 0 3px ${accentTheme.ring}`,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(255,255,255,0.08)",
        },
      },
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
  },
  palette: {
    mode: "light",
    primary: {
      main: accentTheme.primary,
    },
    text: {
      primary: "#121212",
      secondary: "#4f4f4f",
      body: accentTheme.primary,
      watermark: "#00000014",
    },
    background: {
      default: "#faf8f4",
      paper: "#ffffff",
      dark: "#ffffff",
    },
    action: {
      hover: "#FF8F07",
    },
    accent: {
      main: accentTheme.primary,
      light: accentTheme.lightSurface,
      dark: accentTheme.hover,
    },
  },
});

export const topWritersList = (allBlogs) => {
  const writers = allBlogs.reduce((acc, blog) => {
    const name = blog?.username || "Anonymous";

    if (!acc[name]) {
      acc[name] = {
        name,
        role: blog?.userrole || blog?.category || "Contributor",
        totalBlogs: 0,
        totalLikes: 0,
        totalComments: 0,
      };
    }

    acc[name].totalBlogs += 1;
    acc[name].totalLikes += blog?.likes?.length || 0;
    acc[name].totalComments += blog?.comments?.length || 0;
    return acc;
  }, {});

  return Object.values(writers)
    .sort((a, b) => {
      if (b.totalBlogs !== a.totalBlogs) return b.totalBlogs - a.totalBlogs;
      if (b.totalLikes !== a.totalLikes) return b.totalLikes - a.totalLikes;
      return b.totalComments - a.totalComments;
    })
    .slice(0, 4);
};

export const mode = JSON.parse(localStorage.getItem("theme"));
